import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Label } from '@/shared/components/ui/label';
import { Textarea } from '@/shared/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { formatMoney } from '@/shared/lib/money';
import { listActiveMediosDePago } from '@/shared/api/controllers/mediosDePago';
import {
  acreditarEcheq,
  autorizarEcheq,
  rechazarEcheq,
  rechazoBancoEcheq,
  type Echeq,
} from '@/shared/api/controllers/echeqs';

/**
 * Diálogos del flujo E-cheq (AR), compartidos por Aprobaciones (autorizar /
 * rechazar) y Administración → E-cheqs (acreditar / rechazo del banco).
 */

export const fechaAr = (iso: string | null | undefined) =>
  iso ? iso.slice(0, 10).split('-').reverse().join('/') : '—';

/** Lo que se toca después de cualquier acción sobre un E-cheq. */
function useRefrescar() {
  const qc = useQueryClient();
  return () => {
    for (const k of ['echeqs', 'aprobaciones', 'administracion', 'pagos', 'ventas', 'tesoreria']) {
      qc.invalidateQueries({ queryKey: [k] });
    }
  };
}

export function EcheqResumen({ echeq }: { echeq: Echeq }) {
  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-1 rounded-lg border bg-muted/40 px-3 py-2 text-xs">
      <span className="text-muted-foreground">Venta</span>
      <span className="font-medium text-foreground">
        {echeq.ventaId} — {echeq.cliente ?? '—'}
      </span>
      <span className="text-muted-foreground">Monto</span>
      <span className="font-semibold text-foreground">{formatMoney(echeq.monto, 'ARS')}</span>
      <span className="text-muted-foreground">N° de cheque</span>
      <span className="text-foreground">{echeq.nroCheque}</span>
      <span className="text-muted-foreground">Banco emisor</span>
      <span className="text-foreground">{echeq.bancoEmisor}</span>
      <span className="text-muted-foreground">CUIT librador</span>
      <span className="text-foreground">{echeq.cuitLibrador}</span>
      <span className="text-muted-foreground">Fecha de pago</span>
      <span className="text-foreground">{fechaAr(echeq.fechaPago)}</span>
    </div>
  );
}

export function AutorizarEcheqDialog({ echeq, onClose }: { echeq: Echeq; onClose: () => void }) {
  const refrescar = useRefrescar();
  const mut = useMutation({
    mutationFn: () => autorizarEcheq(echeq.id),
    onSuccess: () => {
      toast.success('E-cheq autorizado', {
        description: 'Queda en Administración → E-cheqs hasta que se acredite.',
      });
      refrescar();
      onClose();
    },
    onError: (err) =>
      toast.error('No se pudo autorizar', { description: err instanceof Error ? err.message : '' }),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && !mut.isPending && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Autorizar E-cheq</DialogTitle>
          <DialogDescription>
            La venta cuenta como pagada recién cuando el cheque se acredite en el banco.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <EcheqResumen echeq={echeq} />
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={mut.isPending}>
            Cancelar
          </Button>
          <Button onClick={() => mut.mutate()} disabled={mut.isPending}>
            {mut.isPending ? 'Autorizando…' : 'Autorizar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Rechazo de la autorización o del banco: los dos anulan el cobro. */
export function RechazarEcheqDialog({
  echeq,
  modo,
  onClose,
}: {
  echeq: Echeq;
  modo: 'autorizacion' | 'banco';
  onClose: () => void;
}) {
  const refrescar = useRefrescar();
  const [motivo, setMotivo] = useState('');
  const mut = useMutation({
    mutationFn: () =>
      modo === 'banco' ? rechazoBancoEcheq(echeq.id, motivo.trim()) : rechazarEcheq(echeq.id, motivo.trim()),
    onSuccess: () => {
      toast.success(modo === 'banco' ? 'E-cheq rechazado por el banco' : 'E-cheq rechazado', {
        description: 'El cobro quedó anulado: la venta vuelve a deber ese monto.',
      });
      refrescar();
      onClose();
    },
    onError: (err) =>
      toast.error('No se pudo rechazar', { description: err instanceof Error ? err.message : '' }),
  });
  const motivoValido = motivo.trim().length >= 3;
  return (
    <Dialog open onOpenChange={(o) => !o && !mut.isPending && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{modo === 'banco' ? 'Rechazado por el banco' : 'Rechazar E-cheq'}</DialogTitle>
          <DialogDescription>Se anula el cobro y la venta vuelve a deber ese monto.</DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <EcheqResumen echeq={echeq} />
          <div className="space-y-1">
            <Label htmlFor="motivo-echeq" className="text-sm">
              Motivo
            </Label>
            <Textarea
              id="motivo-echeq"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              disabled={mut.isPending}
              rows={3}
              maxLength={500}
              placeholder={modo === 'banco' ? 'Ej.: sin fondos' : 'Por qué no se acepta este E-cheq'}
            />
            <p className="text-xs text-muted-foreground">Lo ve la vendedora en la venta.</p>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={mut.isPending}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={() => mut.mutate()}
            disabled={mut.isPending || !motivoValido}
            title={motivoValido ? undefined : 'Escribí el motivo'}
          >
            {mut.isPending ? 'Rechazando…' : 'Rechazar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Acreditar: elegir el banco donde entró; confirma el cobro ahí. */
export function AcreditarEcheqDialog({ echeq, onClose }: { echeq: Echeq; onClose: () => void }) {
  const refrescar = useRefrescar();
  const mediosQ = useQuery({
    queryKey: ['echeqs', 'bancos'],
    queryFn: listActiveMediosDePago,
    staleTime: 5 * 60_000,
  });
  // Bancos en pesos activos (el E-cheq es en pesos).
  const bancos = useMemo(
    () =>
      (mediosQ.data ?? []).filter(
        (m) =>
          m.fields.Tipo_MDP === 'Banco' &&
          m.fields.Categoria_MDP !== 'Internacional' &&
          m.fields.Status_MDP === 'Activo',
      ),
    [mediosQ.data],
  );
  const [caja, setCaja] = useState('');
  const mut = useMutation({
    mutationFn: () => acreditarEcheq(echeq.id, caja),
    onSuccess: (r) => {
      toast.success('E-cheq acreditado', {
        description: `Cobro confirmado en ${caja}${r.statusRO ? ` · venta en ${r.statusRO}` : ''}.`,
      });
      refrescar();
      onClose();
    },
    onError: (err) =>
      toast.error('No se pudo acreditar', { description: err instanceof Error ? err.message : '' }),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && !mut.isPending && onClose()}>
      <DialogContent className="max-w-md" processing={mut.isPending && 'Acreditando…'}>
        <DialogHeader>
          <DialogTitle>Acreditar E-cheq</DialogTitle>
          <DialogDescription>
            El cobro se confirma en el banco elegido, con sus impuestos, y la venta avanza como
            con cualquier cobro confirmado.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <EcheqResumen echeq={echeq} />
          <div className="space-y-1">
            <Label className="text-sm">Banco donde se acreditó</Label>
            <Select value={caja} onValueChange={setCaja} disabled={mediosQ.isPending}>
              <SelectTrigger aria-label="Banco donde se acreditó">
                <SelectValue placeholder={mediosQ.isPending ? 'Cargando…' : 'Elegir banco'} />
              </SelectTrigger>
              <SelectContent>
                {bancos.map((b) => (
                  <SelectItem key={b.id} value={b.fields.Medio_MDP ?? ''}>
                    {b.fields.Medio_MDP}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={mut.isPending}>
            Cancelar
          </Button>
          <Button onClick={() => mut.mutate()} disabled={mut.isPending || !caja}>
            Acreditar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
